import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { AlertCircle, CheckCircle2, Eye, EyeOff, Loader2 } from "lucide-react";
import { GLOBAL_IMAGES } from "../../assets/asset";
import { extractStudentEmail, studentAuthApi } from "../../features/auth/authApi";
import {
  clearStudentResetContext,
  getStoredStudentResetContext,
  persistStudentResetContext,
} from "../../features/auth/studentSession";

const GENERIC_INVALID_LINK = "Unable to validate this reset link.";
const GENERIC_RESET_SUCCESS = "Password updated successfully.";
const GENERIC_RESET_FAILURE = "Unable to complete the request right now.";

const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const storedResetContext = useMemo(() => getStoredStudentResetContext(), []);
  const resetId = String(searchParams.get("id") || storedResetContext.resetId || "").trim();
  const resetToken = String(searchParams.get("token") || storedResetContext.resetToken || "").trim();
  const [email, setEmail] = useState(storedResetContext.email || "");
  const [formData, setFormData] = useState({
    password: "",
    confirmPassword: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState("");

  const verifyLinkQuery = useQuery({
    queryKey: ["student-reset-link", resetId, resetToken],
    queryFn: () =>
      studentAuthApi.verifyResetLink({
        id: resetId,
        token: resetToken,
      }),
    enabled: Boolean(resetId && resetToken),
    retry: false,
  });

  useEffect(() => {
    if (!resetId || !resetToken) return;

    persistStudentResetContext({
      email,
      resetId,
      resetToken,
    });
  }, [email, resetId, resetToken]);

  useEffect(() => {
    const responseEmail = extractStudentEmail(verifyLinkQuery.data);
    if (responseEmail) {
      setEmail(responseEmail);
      persistStudentResetContext({
        email: responseEmail,
        resetId,
        resetToken,
      });
    }
  }, [resetId, resetToken, verifyLinkQuery.data]);

  const resetPasswordMutation = useMutation({
    mutationFn: studentAuthApi.submitNewPassword,
    onSuccess: () => {
      clearStudentResetContext();
      setSuccessMessage(GENERIC_RESET_SUCCESS);
      setErrors({});
      setFormData({
        password: "",
        confirmPassword: "",
      });
      window.setTimeout(() => {
        navigate("/login", { replace: true });
      }, 1200);
    },
    onError: (error) => {
      console.error("submitNewPassword failed", error?.response?.data || error);
      setSuccessMessage("");
      setErrors({
        submit: GENERIC_RESET_FAILURE,
      });
    },
  });

  const handleSubmit = (event) => {
    event.preventDefault();

    const normalizedEmail = String(email || "").trim().toLowerCase();
    const nextErrors = {};

    if (!resetId || !resetToken) {
      nextErrors.submit = GENERIC_INVALID_LINK;
    }

    if (!normalizedEmail) {
      nextErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizedEmail)) {
      nextErrors.email = "Please enter a valid email address";
    }

    if (!formData.password) {
      nextErrors.password = "Password is required";
    }

    if (!formData.confirmPassword) {
      nextErrors.confirmPassword = "Please confirm your password";
    } else if (formData.password !== formData.confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match";
    }

    if (Object.keys(nextErrors).length) {
      setErrors(nextErrors);
      return;
    }

    setErrors({});
    setSuccessMessage("");
    resetPasswordMutation.mutate({
      email: normalizedEmail,
      password: formData.password,
      token: resetToken,
    });
  };

  const isLinkInvalid =
    !resetId ||
    !resetToken ||
    verifyLinkQuery.isError ||
    verifyLinkQuery.data?.data?.valid === false ||
    verifyLinkQuery.data?.data?.is_valid === false ||
    verifyLinkQuery.data?.valid === false ||
    verifyLinkQuery.data?.is_valid === false;

  return (
    <div
      className="flex flex-col items-center justify-center font-mali min-h-screen"
      style={{
        backgroundImage: `url(${GLOBAL_IMAGES.onboarding_bg})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    >
      <div className="absolute top-6 left-6">
        <img
          src={GLOBAL_IMAGES.dingo}
          alt="Mathdingo Logo"
          className="w-20 h-20"
        />
      </div>

      <div className="w-full max-w-xl px-4">
        <div className="bg-white/60 rounded-3xl p-8 shadow-lg">
          <div className="flex items-center gap-4 mb-8">
            <img
              src={GLOBAL_IMAGES.LoginBackButton}
              alt="Back"
              className="w-14 h-14 cursor-pointer"
              onClick={() => navigate("/forgot-password")}
            />
            <h2 className="text-4xl font-bold">Reset Password</h2>
          </div>

          {verifyLinkQuery.isLoading ? (
            <div className="flex items-center gap-3 rounded-2xl border border-blue-200 bg-blue-50 px-4 py-4 text-blue-700">
              <Loader2 className="animate-spin" size={20} />
              <span>Validating your reset link...</span>
            </div>
          ) : null}

          {isLinkInvalid && !verifyLinkQuery.isLoading ? (
            <div className="space-y-5">
              <div className="flex items-center gap-2 rounded-2xl border border-red-200 bg-red-50 px-4 py-4 text-red-700">
                <AlertCircle size={18} />
                <span>{GENERIC_INVALID_LINK}</span>
              </div>
              <Link
                to="/forgot-password"
                className="inline-flex w-full items-center justify-center rounded-full bg-blue-800 px-6 py-3 text-lg font-bold text-white transition hover:bg-blue-900"
              >
                Request a new reset link
              </Link>
            </div>
          ) : null}

          {!isLinkInvalid && !verifyLinkQuery.isLoading ? (
            <form onSubmit={handleSubmit} className="space-y-5">
              <p className="text-gray-700">
                Choose a new password for your account.
              </p>

              <div>
                <label className="block text-xl font-bold text-gray-700 mb-2" htmlFor="reset-email">
                  Email
                </label>
                <input
                  id="reset-email"
                  type="email"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setErrors((previous) => ({ ...previous, email: "", submit: "" }));
                  }}
                  className="w-full px-4 py-3 rounded-full border-2 border-gray-300 focus:outline-none focus:border-blue-500"
                  placeholder="Enter your email address"
                  autoComplete="email"
                />
                {errors.email ? (
                  <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                    <AlertCircle size={16} />
                    <span>{errors.email}</span>
                  </div>
                ) : null}
              </div>

              <div>
                <label className="block text-xl font-bold text-gray-700 mb-2" htmlFor="reset-password">
                  New Password
                </label>
                <div className="relative">
                  <input
                    id="reset-password"
                    type={showPassword ? "text" : "password"}
                    value={formData.password}
                    onChange={(event) => {
                      setFormData((previous) => ({
                        ...previous,
                        password: event.target.value,
                      }));
                      setErrors((previous) => ({ ...previous, password: "", submit: "" }));
                    }}
                    className="w-full px-4 py-3 rounded-full border-2 border-gray-300 focus:outline-none focus:border-blue-500 pr-12"
                    placeholder="Enter your new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((previous) => !previous)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                  </button>
                </div>
                {errors.password ? (
                  <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                    <AlertCircle size={16} />
                    <span>{errors.password}</span>
                  </div>
                ) : null}
              </div>

              <div>
                <label
                  className="block text-xl font-bold text-gray-700 mb-2"
                  htmlFor="reset-confirm-password"
                >
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    id="reset-confirm-password"
                    type={showConfirmPassword ? "text" : "password"}
                    value={formData.confirmPassword}
                    onChange={(event) => {
                      setFormData((previous) => ({
                        ...previous,
                        confirmPassword: event.target.value,
                      }));
                      setErrors((previous) => ({
                        ...previous,
                        confirmPassword: "",
                        submit: "",
                      }));
                    }}
                    className="w-full px-4 py-3 rounded-full border-2 border-gray-300 focus:outline-none focus:border-blue-500 pr-12"
                    placeholder="Confirm your new password"
                    autoComplete="new-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword((previous) => !previous)}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500"
                  >
                    {showConfirmPassword ? <EyeOff size={22} /> : <Eye size={22} />}
                  </button>
                </div>
                {errors.confirmPassword ? (
                  <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                    <AlertCircle size={16} />
                    <span>{errors.confirmPassword}</span>
                  </div>
                ) : null}
              </div>

              {errors.submit ? (
                <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-red-50 border-2 border-red-300 rounded-full text-red-700 text-sm">
                  <AlertCircle size={16} />
                  <span>{errors.submit}</span>
                </div>
              ) : null}

              {successMessage ? (
                <div className="flex items-center gap-2 mt-2 px-4 py-2 bg-green-50 border-2 border-green-300 rounded-full text-green-700 text-sm">
                  <CheckCircle2 size={16} />
                  <span>{successMessage}</span>
                </div>
              ) : null}

              <button
                type="submit"
                disabled={resetPasswordMutation.isPending}
                className="w-full bg-blue-800 text-white py-3 rounded-full hover:bg-blue-900 transition-colors text-xl font-semibold flex items-center justify-center disabled:opacity-70"
              >
                {resetPasswordMutation.isPending ? (
                  <Loader2 className="animate-spin" size={24} />
                ) : (
                  "Update Password"
                )}
              </button>
            </form>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordPage;
